import { StatusBadge } from './StatusBadge';
import {
  phaseLabel,
  phaseOf,
  phaseTone,
  timeAgo,
  truncate,
  type CheckPhase,
  type VerificationViewDoc,
} from '../lib/verificationView';

const DETAIL_MAX = 140;

const KNOWN: readonly string[] = [
  'verified',
  'not_verified',
  'excluded',
  'manual_review',
  'inconclusive',
  'failed',
];

/** Source-level status → badge. Source statuses (e.g. `no_match`, `source_error`) are free text. */
function evidenceTone(status: string) {
  if (KNOWN.includes(status)) return phaseTone(status as CheckPhase);
  if (status === 'no_match') return 'bad' as const;
  if (status === 'source_error') return 'warn' as const;
  return 'neutral' as const;
}

/** Only absolute http(s) URLs become links; anything else (javascript:, data:, relative) stays text. */
function safeLink(reference: string): { href: string; host: string } | null {
  try {
    const u = new URL(reference);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname) return null;
    return { href: u.href, host: u.hostname };
  } catch {
    return null;
  }
}

function Reference({ value }: { value?: string | null }) {
  if (!value) return null;
  const link = safeLink(value.trim());
  if (!link) return <>{value}</>;
  return (
    <a href={link.href} rel="noopener noreferrer" target="_blank">
      {link.host}
    </a>
  );
}

/** Everything the validator found, source by source. All values are rendered as text. */
export function EvidencePanel({
  view,
  jobError,
}: {
  view: VerificationViewDoc;
  jobError?: string | null;
}) {
  const evidence = view.evidence ?? [];
  const phase = phaseOf(view);
  const needsManual = phase === 'manual_review' || phase === 'inconclusive' || phase === 'failed';
  // Only a failed check has a current error; an older one must not linger, and it is already
  // shown as the reason when the failure came from a result.
  const showJobError = view.jobStatus === 'failed' && !!jobError && jobError !== view.reason;
  return (
    <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px', marginTop: '0.75rem' }}>
      {view.reason && <p style={{ marginTop: 0 }}>{view.reason}</p>}
      {showJobError && <p className="error-banner">Last run failed: {jobError}</p>}
      {needsManual && (
        <div role="note" style={{ marginBottom: '0.75rem' }}>
          <strong>Manual verification</strong>
          <p style={{ margin: '0.25rem 0 0' }}>
            The automated check could not confirm this specialist. Open the official board link in the
            table below and check the licence number, name, status and any discipline. Then use Approve
            or Reject on this page and add a note.
          </p>
        </div>
      )}
      {evidence.length === 0 ? (
        <p style={{ margin: 0, opacity: 0.7 }}>No evidence yet.</p>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Source</th>
              <th>Result</th>
              <th>Checked</th>
              <th>Official link</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {evidence.map((e, i) => (
              <tr key={`${e.source}-${i}`}>
                <td>{e.source}</td>
                <td>
                  <StatusBadge label={e.status} tone={evidenceTone(e.status)} />
                </td>
                <td>{timeAgo(e.checkedAt)}</td>
                <td>
                  <Reference value={e.reference} />
                </td>
                <td title={e.detail ?? undefined}>{truncate(e.detail ?? '', DETAIL_MAX)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {view.nextCheckAt && !Number.isNaN(Date.parse(view.nextCheckAt)) && (
        <small style={{ opacity: 0.7 }}>Next scheduled check: {new Date(view.nextCheckAt).toLocaleDateString()}</small>
      )}
      {view.jobId && (
        <small style={{ display: 'block', opacity: 0.5 }}>
          {phaseLabel(view.status ?? 'not_started')} · job {view.jobId} g{view.generation ?? 1}
        </small>
      )}
    </div>
  );
}
